import { useState } from "react";
import { X, CheckCircle, Loader2, ArrowRight } from "lucide-react";
import FlexxLogoImg from "../FlexxLogoImg";

const EmailCaptureModal = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [telegram, setTelegram] = useState("");
  const [referredBy, setReferredBy] = useState("");
  const [howFound, setHowFound] = useState("");
  const [status, setStatus] = useState("idle"); // idle, loading, success, error
  const [errorMessage, setErrorMessage] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setErrorMessage("");

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/subscribe`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, name, whatsapp: whatsapp || null, telegram: telegram || null, referred_by: referredBy || null, how_found: howFound || null }),
      });

      const text = await response.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        data = { detail: text };
      }
      
      if (!response.ok) {
        setStatus("error");
        setErrorMessage(data.detail || "Something went wrong");
        return;
      }

      setStatus("success");
      setEmail("");
      setName("");
      setWhatsapp("");
      setTelegram("");
      setReferredBy("");
      setHowFound("");
    } catch (error) {
      setStatus("error");
      setErrorMessage("Network error. Please try again.");
    }
  };

  const handleClose = () => {
    setStatus("idle");
    setErrorMessage("");
    setEmail("");
    setName("");
    setWhatsapp("");
    setTelegram("");
    setReferredBy("");
    setHowFound("");
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose} data-testid="email-modal-overlay">
      <div 
        className="modal-container" 
        onClick={(e) => e.stopPropagation()}
        data-testid="email-modal"
      >
        <button 
          className="modal-close" 
          onClick={handleClose}
          aria-label="Close modal"
          data-testid="modal-close-btn"
        >
          <X size={24} />
        </button>

        {status === "success" ? (
          <div className="modal-success" data-testid="modal-success">
            <CheckCircle className="success-icon" size={64} />
            <h3>🎉 You're Almost In!</h3>
            <div className="success-body">
              <p className="success-lead">Thank you for registering for Flexx Academy!</p>
              <p className="success-emphasis">Your next step is IMPORTANT — don't skip it:</p>
              <p>📧 Check your email inbox right now (and your spam/junk folder just in case).</p>
              <p>
                Look for an email from FLEXX Academy with the subject line{" "}
                <strong>"You're in — Welcome to FLEXX Academy 🎓"</strong>
              </p>
              <p>
                Click <strong>"START ONBOARDING"</strong> in that email to complete your application and secure your spot.
              </p>
              <p className="success-warning">⚡ Your spot is not confirmed until you complete the onboarding form.</p>
            </div>
            <button className="btn-primary modal-btn" onClick={handleClose} data-testid="modal-success-btn">
              <span>✅ Got it — Checking My Email Now!</span>
            </button>
          </div>
        ) : (
          <>
            <div className="modal-header">
              <FlexxLogoImg className="modal-logo" alt="FLEXX Academy" />
              <h2>Join the FLEXX Academy Community</h2>
              <p>Get access to educational content, collaboration updates, and community insights.</p>
            </div>

            <form onSubmit={handleSubmit} className="modal-form" data-testid="email-form">
              <div className="form-group">
                <label htmlFor="name">Name *</label>
                <input
                  type="text"
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  required
                  className="form-input"
                  data-testid="name-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">Email address *</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  className="form-input"
                  data-testid="email-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="whatsapp">WhatsApp / Mobile *</label>
                <input
                  type="tel"
                  id="whatsapp"
                  value={whatsapp}
                  onChange={(e) => setWhatsapp(e.target.value)}
                  placeholder="+1 (555) 000-0000"
                  required
                  className="form-input"
                  data-testid="whatsapp-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="telegram">Telegram Username *</label>
                <input
                  type="text"
                  id="telegram"
                  value={telegram}
                  onChange={(e) => setTelegram(e.target.value)}
                  placeholder="@username"
                  required
                  className="form-input"
                  data-testid="telegram-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="referredBy">Referred By</label>
                <input
                  type="text"
                  id="referredBy"
                  value={referredBy}
                  onChange={(e) => setReferredBy(e.target.value)}
                  placeholder="Name of person who referred you"
                  className="form-input"
                  data-testid="referred-by-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="howFound">How did you find Flexx? *</label>
                <select
                  id="howFound"
                  value={howFound}
                  onChange={(e) => setHowFound(e.target.value)}
                  required
                  className="form-input"
                  data-testid="how-found-select"
                >
                  <option value="" disabled>Select an option</option>
                  <option value="Event / QR code">Event / QR code</option>
                  <option value="Instagram">Instagram</option>
                  <option value="Facebook">Facebook</option>
                  <option value="TikTok">TikTok</option>
                  <option value="Friend / Referral">Friend / Referral</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {status === "error" && (
                <p className="form-error" data-testid="form-error">{errorMessage}</p>
              )}

              <button 
                type="submit" 
                className="btn-primary modal-btn"
                disabled={status === "loading"}
                data-testid="submit-btn"
              >
                {status === "loading" ? (
                  <>
                    <Loader2 className="spinner" size={20} />
                    <span>Joining...</span>
                  </>
                ) : (
                  <>
                    <span>Join the Community</span>
                    <ArrowRight className="btn-icon" size={16} />
                  </>
                )}
              </button>

              <p className="form-disclaimer">
                No spam, ever. Only educational updates and community news.
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default EmailCaptureModal;
